<template>
  <button
    v-if="visible"
    type="button"
    :aria-label="t('msg.appLogExport.button')"
    :title="t('msg.appLogExport.button')"
    class="inline-flex h-10 w-10 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 lg:h-11 lg:w-11"
    @click="open = true"
  >
    <DocumentArrowUpIcon class="h-4 w-4 md:h-5 md:w-5" />
  </button>

  <Dialog v-if="visible && open" :show="true" class="fixed inset-0 z-50" @close="closeDialog">
    <DialogOverlay class="fixed inset-0 bg-black/50" />

    <DialogPanel :theme="theme" :high-contrast="highAccessibility" class="flex w-full max-w-xl flex-col gap-6 p-8">
      <DialogTitle class="text-2xl font-bold">
        {{ t("msg.appLogExport.title") }}
      </DialogTitle>

      <DialogDescription class="text-base text-foreground">
        <p>{{ t("msg.appLogExport.body") }}</p>
        <p v-if="state === 'sent'" class="mt-3 text-sm text-emerald-500">
          {{ t("msg.appLogExport.sent") }}
        </p>
        <p v-if="state === 'error' && lastError" class="mt-3 text-sm text-danger">
          {{ t("msg.appLogExport.failed", { reason: lastError }) }}
        </p>
      </DialogDescription>

      <div class="flex flex-col gap-3 sm:flex-row sm:justify-end">
        <ThemedButton type="button" variant="outline" :disabled="busy" @click="onSave">
          <span v-if="state === 'saving'">{{ t("msg.appLogExport.saving") }}</span>
          <span v-else>{{ t("msg.appLogExport.saveToDevice") }}</span>
        </ThemedButton>
        <ThemedButton type="button" :disabled="busy" @click="onSend">
          <span v-if="state === 'collecting'">{{ t("msg.appLogExport.collecting") }}</span>
          <span v-else-if="state === 'uploading'">{{ t("msg.appLogExport.uploading") }}</span>
          <span v-else>{{ t("msg.appLogExport.sendNow") }}</span>
        </ThemedButton>
      </div>
    </DialogPanel>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { DocumentArrowUpIcon } from "@heroicons/vue/24/outline";

import { DialogDescription } from "@headlessui/vue";

import { Capacitor } from "@capacitor/core";

import { dumpDebugLogsGzipped, isDebugBuildMode, isDebugLogsAvailable, shareDebugLogs } from "../capacitor/debug-logs";
import { isNativePlatform } from "../capacitor/platform";
import globals from "../globals";
import { useThemeStore } from "../pinia/theme.ts";
import appLogsService from "../services/app-logs.service";
import Dialog from "./ui/dialog/Dialog.vue";
import DialogOverlay from "./ui/dialog/DialogOverlay.vue";
import DialogPanel from "./ui/dialog/DialogPanel.vue";
import DialogTitle from "./ui/dialog/DialogTitle.vue";
import ThemedButton from "../user/components/ThemedButton.vue";

type ExportState = "idle" | "saving" | "collecting" | "uploading" | "sent" | "error";

const { t } = useI18n();
const themeStore = useThemeStore();
themeStore.fetchTheme();
const theme = computed(() => themeStore.accent);
const highAccessibility = computed(() => themeStore.highAccessibility);

// Shown only when BOTH hold:
//  - isDebugBuildMode(): a dev or prerelease web bundle. A production bundle
//    hides the button on any installed APK, so deploying a production frontend
//    (OTA) is enough to remove it.
//  - isDebugLogsAvailable(): the native DebugLogs plugin is registered, which
//    only happens in debug-type APKs. This avoids showing a dead button when a
//    prerelease bundle rides on a release APK (plugin absent).
// Both layers are false in the Play/App Store release artifact.
const visible = isDebugBuildMode() && isDebugLogsAvailable();
const open = ref(false);
const state = ref<ExportState>("idle");
const lastError = ref<string | null>(null);
const busy = computed(() => state.value === "saving" || state.value === "collecting" || state.value === "uploading");

function resetTransientState() {
  if (state.value === "sent" || state.value === "error") {
    state.value = "idle";
    lastError.value = null;
  }
}

function closeDialog() {
  if (busy.value) return;
  open.value = false;
  resetTransientState();
}

async function getNativeAppInfo(): Promise<{ version: string; platform: string }> {
  if (!isNativePlatform()) return { version: "web", platform: "web" };
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    return { version: info.version ?? "unknown", platform: Capacitor.getPlatform() };
  } catch {
    return { version: "unknown", platform: "unknown" };
  }
}

async function onSave() {
  if (busy.value) return;
  state.value = "saving";
  lastError.value = null;
  try {
    const result = await shareDebugLogs();
    if (result === null) {
      state.value = "error";
      lastError.value = t("msg.appLogExport.collectFailed");
      return;
    }
    state.value = "idle";
    open.value = false;
  } catch (err: unknown) {
    state.value = "error";
    lastError.value = globals.renderError(t, err);
  }
}

async function onSend() {
  if (busy.value) return;
  state.value = "collecting";
  lastError.value = null;
  const dump = await dumpDebugLogsGzipped();
  if (!dump) {
    state.value = "error";
    lastError.value = t("msg.appLogExport.collectFailed");
    return;
  }
  state.value = "uploading";
  try {
    const info = await getNativeAppInfo();
    await appLogsService.uploadAppLogBundle({
      gzippedBase64: dump.data,
      appVersion: info.version,
      platform: info.platform,
    });
    state.value = "sent";
  } catch (err: unknown) {
    state.value = "error";
    lastError.value = globals.renderError(t, err);
  }
}
</script>
